import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { FormValues, TOAST_SUCCESS_MESSAGE, Validation } from './ClientSettingsForm.meta';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { Select } from '../../../CommonComponents/OPL/Select/Select/Select';
import { Checkbox } from '../../../CommonComponents/OPL/Checkbox/Checkbox';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Integrated } from './Integrated';
import {
  selectAssetBucketingHeadline,
  selectIsCanadian,
} from '../../../CommonSelectors/CommonAreaSelector';
import { AsyncData } from '../../useClientSettings';
import { useClientSettingsMutation } from '../../api/useClientSettingsMutation';
import { MESSAGES } from '../../../CommonConstants';
import { FullPageLoading } from '../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { yupResolver } from '@hookform/resolvers/yup';
import { InputNumberMask } from '../../../CommonComponents/OPL/Input/InputNumberMask';
import { CurrentYearAssumptionSetting } from '../../../CommonComponents/CurrentYearAssumptionSetting/CurrentYearAssumptionSetting';
import { PlanStartYearDTO } from '../../api/useClientSettingsQuery';

interface Props {
  data: AsyncData;
}

export const ClientSettingsForm: React.FC<Props> = ({ data }) => {
  const [showCurrentYearAssumptionSetting, setCurrentYearAssumptionSettingVisibility] = useState(
    false,
  );
  const assetBucketingHeadline = useSelector(selectAssetBucketingHeadline);

  const isCanadian = useSelector(selectIsCanadian);

  const { options, settings } = data;

  const formBag = useForm<FormValues>({
    resolver: yupResolver(Validation),
    defaultValues: {
      Inflation: settings.Inflation,
      LifeExpectancyPrincipal: settings.LifeExpectancyPrincipal,
      PortfolioReturnsMethodology: settings.PortfolioReturnsMethodology,
      SocialSecurityInflation: settings.SocialSecurityInflation,
      LifeExpectancySpouse: settings.LifeExpectancySpouse,
      EmployerId: settings.EmployerId,
      EducationInflation: settings.EducationInflation,
      AllowAssetBucketing: settings.AllowAssetBucketing,
      LoanBalanceInterest: settings.LoanBalanceInterest,
      CashflowEngine: settings.CashflowEngine,
      SocialSecurityUseSpousalBenefit: settings.SocialSecurityUseSpousalBenefit,
      SocialSecurityFileForSurvivorBenefits: settings.SocialSecurityFileForSurvivorBenefits,
      AllowBeFi20: settings.AllowBeFi20,
      RiskToleranceWorkflow: settings.RiskToleranceWorkflow,
      CapitalMarketAssumptions: settings.CapitalMarketAssumptions,
      AllowPulseCheck: settings.AllowPulseCheck,
      PlanStartYear: settings.PlanStartYear,
      ExcessCashFlowPercentToSave: settings.ExcessCashFlowPercentToSave,
      MonteCarloSuccessRange: settings.MonteCarloSuccessRange,
    },
  });

  const { watch } = formBag;

  const planStartYear = watch('PlanStartYear');

  const {
    mutateAsync: updateClientSettings,
    isLoading: isUpdatingClientSettings,
  } = useClientSettingsMutation();

  const onSubmit = async (values: FormValues) => {
    try {
      await updateClientSettings(values);

      toast.success(TOAST_SUCCESS_MESSAGE);
    } catch (err) {
      console.error(err);

      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error(MESSAGES.GENERIC_ERROR);
      }
    }
  };

  return (
    <>
      <FormProvider {...formBag}>
        {isUpdatingClientSettings && <FullPageLoading />}

        <form
          onSubmit={formBag.handleSubmit(onSubmit)}
          css={`
            display: flex;
            flex-direction: column;
            height: 100%;
          `}
        >
          <Box
            display="grid"
            css={`
              grid-template-columns: repeat(3, 1fr);
              row-gap: 32px;
              column-gap: 48px;
            `}
          >
            <Input
              rightIcon="far fa-percent"
              label="Inflation Rate"
              {...formBag.register('Inflation')}
            />

            {settings.PrincipalName && (
              <Input
                innerText="years"
                label={`${settings.PrincipalName} Life Expectancy`}
                {...formBag.register('LifeExpectancyPrincipal')}
              />
            )}

            {settings.AdvisersAssumptionsEnabled && (
              <Controller
                control={formBag.control}
                name="PortfolioReturnsMethodology"
                render={({ field: { value } }) => (
                  <Select
                    value={options.portfolioReturnsOptions.find(
                      ({ value: _value }) => value === _value,
                    )}
                    options={options.portfolioReturnsOptions}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue('PortfolioReturnsMethodology', opt.value);
                      }
                    }}
                    label="Portfolio Returns"
                  />
                )}
              />
            )}

            {!isCanadian && (
              <Input
                rightIcon="far fa-percent"
                label="Social Security Inflation Rate"
                {...formBag.register('SocialSecurityInflation')}
              />
            )}

            {settings.SpouseName && (
              <Input
                innerText="years"
                label={`${settings.SpouseName} Life Expectancy`}
                {...formBag.register('LifeExpectancySpouse')}
              />
            )}

            <Input
              rightIcon="far fa-percent"
              label="Tuition Inflation Rate"
              {...formBag.register('EducationInflation')}
            />

            <Controller
              control={formBag.control}
              name="ExcessCashFlowPercentToSave"
              render={({ field: { value } }) => (
                <InputNumberMask
                  value={value}
                  label="Excess Cash Flow Savings Rate"
                  allowNegative={false}
                  decimalScale={2}
                  rightIcon="far fa-percent"
                  onValueChange={({ floatValue }) => {
                    formBag.setValue(
                      'ExcessCashFlowPercentToSave',
                      floatValue === undefined ? null : floatValue,
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      },
                    );
                  }}
                  tooltip="Enter a value between 0–100, where 0% = Spend, 1-100% = Save"
                  helperText={formBag.formState.errors.ExcessCashFlowPercentToSave?.message || ''}
                  formVariant={
                    formBag.formState.errors.ExcessCashFlowPercentToSave ? 'error' : undefined
                  }
                />
              )}
            />

            {settings.DisplayAssetBucketing && (
              <Controller
                control={formBag.control}
                name="AllowAssetBucketing"
                render={({ field: { value } }) => (
                  <Select
                    value={options.assetBucketingOptions.find(
                      ({ value: _value }) => value === _value,
                    )}
                    options={options.assetBucketingOptions}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue('AllowAssetBucketing', opt.value);
                      }
                    }}
                    label={`Asset Bucketing - ${assetBucketingHeadline}`}
                  />
                )}
              />
            )}

            {settings.DisplayBeFi20 && (
              <Controller
                control={formBag.control}
                name="AllowBeFi20"
                render={({ field: { value } }) => (
                  <Select
                    value={options.beFi20Options.find(({ value: _value }) => value === _value)}
                    options={options.beFi20Options}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue('AllowBeFi20', opt.value);
                      }
                    }}
                    label="BeFi20"
                  />
                )}
              />
            )}

            {settings.DisplayPulseCheck && (
              <Controller
                control={formBag.control}
                name="AllowPulseCheck"
                render={({ field: { value } }) => (
                  <Select
                    value={options.pulseCheckOptions.find(({ value: _value }) => value === _value)}
                    options={options.pulseCheckOptions}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue('AllowPulseCheck', opt.value);
                      }
                    }}
                    label="PulseCheck"
                  />
                )}
              />
            )}

            <Input
              rightIcon="far fa-percent"
              label="Loan Balance Interest Rate"
              {...formBag.register('LoanBalanceInterest')}
            />

            {settings.ShowCashflowEngineVersionSelector && (
              <Controller
                control={formBag.control}
                name="CashflowEngine"
                render={({ field: { value } }) => (
                  <Select
                    value={options.cashflowEngines.find(({ value: _value }) => value === _value)}
                    options={options.cashflowEngines}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue('CashflowEngine', opt.value);
                      }
                    }}
                    label="Cash Flow Simulation"
                  />
                )}
              />
            )}

            {settings.Is3dRiskToleranceWorkflowEnabled && (
              <Controller
                control={formBag.control}
                name="RiskToleranceWorkflow"
                render={({ field: { value, onChange } }) => (
                  <Select
                    label="Risk Questionnaire"
                    value={options.riskToleranceWorkflowOptions.find(
                      ({ value: _value }) => value === _value,
                    )}
                    options={options.riskToleranceWorkflowOptions}
                    onChange={opt => onChange(opt?.value)}
                  />
                )}
              />
            )}

            <Controller
              control={formBag.control}
              name="CapitalMarketAssumptions"
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Capital Market Assumptions"
                  value={options.capitalMarketAssumptionsOptions.find(
                    ({ value: _value }) => value === _value,
                  )}
                  options={options.capitalMarketAssumptionsOptions}
                  onChange={opt => onChange(opt?.value)}
                />
              )}
            />

            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <Box alignSelf="stretch">
                <Controller
                  control={formBag.control}
                  name="PlanStartYear"
                  render={({ field: { value, onChange } }) => (
                    <Select
                      label="Plan Start Year"
                      value={options.planStartYearOptions.find(
                        ({ value: _value }) => value === _value,
                      )}
                      options={options.planStartYearOptions}
                      onChange={opt => onChange(opt?.value)}
                    />
                  )}
                />
              </Box>

              {planStartYear === PlanStartYearDTO.CurrentYear && (
                <Button
                  type="button"
                  variant="link"
                  leftIcon="far fa-pencil"
                  onClick={() => {
                    setCurrentYearAssumptionSettingVisibility(true);
                  }}
                  mt="12"
                >
                  Manage Current Year Assumptions
                </Button>
              )}
            </Box>

            <Controller
              control={formBag.control}
              name="MonteCarloSuccessRange"
              render={({ field: { value, onChange } }) => (
                <InputNumberMask
                  value={value}
                  label="Monte Carlo Success Range"
                  placeholder="Use Advisor Settings"
                  allowNegative={false}
                  decimalScale={0}
                  rightIcon="far fa-percent"
                  onValueChange={({ floatValue }) => {
                    formBag.setValue(
                      'MonteCarloSuccessRange',
                      floatValue === undefined ? null : floatValue,
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      },
                    );
                  }}
                  tooltip="The default success range is 70 - 99%"
                  helperText={formBag.formState.errors.MonteCarloSuccessRange?.message || ''}
                  formVariant={
                    formBag.formState.errors.MonteCarloSuccessRange ? 'error' : undefined
                  }
                />
              )}
            />
          </Box>

          {settings.SpouseName && !isCanadian && (
            <>
              <Checkbox mt="32" {...formBag.register('SocialSecurityUseSpousalBenefit')}>
                File for Social Security Spousal Benefit
              </Checkbox>

              <Checkbox mt="32" {...formBag.register('SocialSecurityFileForSurvivorBenefits')}>
                File for Social Security Survivor Benefit
              </Checkbox>
            </>
          )}

          <Integrated integratedSystems={data.settings.IntegratedSystems} />

          <Box
            width="100%"
            display="flex"
            justifyContent="flex-end"
            borderTop="1px solid"
            borderColor="neutral.200"
            mt="auto"
            mb={-32}
          >
            <Button my="24" variant="subtleInactive" onClick={() => formBag.reset()}>
              Revert
            </Button>

            <Button type="submit" my="24" ml="24">
              Save
            </Button>
          </Box>
        </form>
      </FormProvider>

      {showCurrentYearAssumptionSetting && (
        <CurrentYearAssumptionSetting
          onClose={() => {
            setCurrentYearAssumptionSettingVisibility(false);
          }}
        />
      )}
    </>
  );
};
